import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle } from "lucide-react";
import api from "../../utils/axiosInstance";
import Button from "../Button/Button";

// Everything deleted with the project, in the order the warning lists it.
const GROUPS = [
  { key: "files", label: "Project and job files" },
  { key: "jobs", label: "Jobs" },
  { key: "receivables", label: "Receivables" },
  { key: "payables", label: "Payables" },
  { key: "clientInvoices", label: "Client invoice" },
  { key: "vendorInvoices", label: "Vendor invoices" },
];

const PREVIEW_LIMIT = 5;

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : null;

// "Report.pdf · Project input · 120 KB · 08 Aug 2026"
const recordLine = (record) => [record.title, ...record.meta, fmtDate(record.date)].filter(Boolean).join(" · ");

const blockedReason = (summary) =>
  summary.blockers.includes("payments")
    ? "This project has payments recorded, so it can't be deleted."
    : "This project has invoices only their issuer or the administrator can delete.";

// Delete warning for a project: every item it removes has to be ticked, then confirmed once more.
const DeleteProjectModal = ({ projectId, projectName, projectCode, onCancel, onConfirm }) => {
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState("");
  const [checked, setChecked] = useState({});
  const [expanded, setExpanded] = useState({});
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    api
      .get(`/projects/${projectId}/deletion-summary`)
      .then((res) => setSummary(res.data.data))
      .catch((err) => {
        console.error(err);
        setError("Could not load what this project contains.");
      });
  }, [projectId]);

  useEffect(() => {
    const handleKey = (e) => e.key === "Escape" && !deleting && onCancel();
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onCancel, deleting]);

  const blocked = summary && !summary.canDelete;

  const items = summary
    ? [
        {
          key: "project",
          label: "Project and its details",
          count: null,
          records: [{ key: "project", code: projectCode, title: projectName, meta: [] }],
        },
        ...GROUPS.filter((g) => summary.groups[g.key]?.count > 0).map((g) => ({
          ...g,
          count: summary.groups[g.key].count,
          records: summary.groups[g.key].items,
        })),
      ]
    : [];
  // Payments are permanent: listed so it is clear what stops the delete, never ticked.
  const payments = summary?.groups.payments?.count > 0 ? summary.groups.payments : null;

  const allChecked = items.length > 0 && items.every((item) => checked[item.key]);

  const toggle = (key) => {
    setConfirming(false);
    setChecked((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const toggleAll = () => {
    setConfirming(false);
    setChecked(allChecked ? {} : Object.fromEntries(items.map((item) => [item.key, true])));
  };

  const confirm = async () => {
    setDeleting(true);
    try {
      await onConfirm();
    } finally {
      setDeleting(false);
      setConfirming(false);
    }
  };

  const renderRecords = (key, records, indent) => {
    const showAll = expanded[key];
    const shown = showAll ? records : records.slice(0, PREVIEW_LIMIT);
    return (
      <>
        <ul className={`mt-1 space-y-0.5 ${indent ? "pl-7" : ""}`}>
          {shown.map((record) => (
            <li key={record.key} className="truncate text-xs text-gray-500" title={recordLine(record)}>
              {record.code && <span className="mr-1.5 font-mono text-gray-700">{record.code}</span>}
              {recordLine(record)}
            </li>
          ))}
        </ul>
        {records.length > PREVIEW_LIMIT && (
          <button
            type="button"
            onClick={() => setExpanded((prev) => ({ ...prev, [key]: !showAll }))}
            className={`mt-1 text-xs font-medium text-brand-600 hover:underline ${indent ? "pl-7" : ""}`}
          >
            {showAll ? "Show less" : `Show all ${records.length}`}
          </button>
        )}
      </>
    );
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-gray-900/40 p-4"
      onMouseDown={(e) => e.target === e.currentTarget && !deleting && onCancel()}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="w-full max-w-lg overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-pop"
      >
        <div className="flex items-center gap-3 px-5 pt-5">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-danger-50 text-danger-600">
            <AlertTriangle size={17} strokeWidth={2} />
          </span>
          <h2 className="min-w-0 flex-1 text-sm font-semibold tracking-tight text-gray-900">
            Delete {projectName ? `"${projectName}"` : "this project"}?
          </h2>
        </div>

        <div className="px-5 py-4">
          {error ? (
            <p className="text-[13px] text-danger-600">{error}</p>
          ) : !summary ? (
            <div className="flex justify-center py-4">
              <div className="h-6 w-6 animate-spin rounded-full border border-gray-300 border-t-brand-600" />
            </div>
          ) : (
            <>
              <ul className="max-h-[55vh] divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200">
                {items.map((item) => (
                  <li key={item.key} className="px-3 py-2">
                    <label
                      className={`flex items-center gap-3 text-[13px] font-medium text-gray-800 ${
                        blocked ? "cursor-not-allowed opacity-60" : "cursor-pointer"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={!!checked[item.key]}
                        onChange={() => toggle(item.key)}
                        disabled={blocked || deleting}
                        className="h-4 w-4 rounded border-gray-300 text-danger-600 focus:ring-danger-200"
                      />
                      <span className="flex-1">{item.label}</span>
                      {item.count !== null && <span className="text-xs font-normal text-gray-500">{item.count}</span>}
                    </label>
                    {renderRecords(item.key, item.records, true)}
                  </li>
                ))}
                {payments && (
                  <li className="px-3 py-2">
                    <div className="flex items-center gap-3 text-[13px] font-medium text-gray-800">
                      <span className="flex-1">Payments</span>
                      <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-600">
                        Can't be deleted
                      </span>
                      <span className="text-xs font-normal text-gray-500">{payments.count}</span>
                    </div>
                    {renderRecords("payments", payments.items, false)}
                  </li>
                )}
              </ul>
              {!blocked && (
                <button
                  type="button"
                  onClick={toggleAll}
                  disabled={deleting}
                  className="mt-2 text-xs font-medium text-brand-600 hover:underline"
                >
                  {allChecked ? "Clear all" : "Tick all"}
                </button>
              )}
              {summary.linkedQuotation && (
                <p className="mt-3 text-xs text-gray-500">
                  Quotation {summary.linkedQuotation} is kept and unlinked from this project.
                </p>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-100 bg-gray-50/70 px-5 py-3">
          {confirming ? (
            <>
              <span className="mr-auto text-[13px] font-medium text-gray-800">Are you sure?</span>
              <Button type="button" variant="secondary" size="sm" onClick={() => setConfirming(false)} disabled={deleting}>
                No
              </Button>
              <Button type="button" variant="danger" size="sm" onClick={confirm} disabled={deleting}>
                {deleting ? "Deleting..." : "Yes, delete"}
              </Button>
            </>
          ) : (
            <>
              <Button type="button" variant="secondary" size="sm" onClick={onCancel}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="danger"
                size="sm"
                onClick={() => setConfirming(true)}
                disabled={blocked || !allChecked}
                title={blocked ? blockedReason(summary) : undefined}
              >
                Delete project
              </Button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default DeleteProjectModal;
